/*
 * Split array into N equal chunks
 */
import { FormGroup } from "@angular/forms";
import { NgbDate, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment";
import slugify from "slugify";

/*
 * Extract a readable message from an HttpErrorResponse regardless of whether the
 * backend sent `{error: 'text'}` or JsonResponse's `{error: {message: 'text'}}` shape.
 */
export function apiErrorMessage(error: any, fallback: string): string {
    const err = error?.error?.error;
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return fallback;
}

// Same local/testing/staging/production split used by the .env-badge classes
// (frontend/src/styles.scss) — shared by the navbar and the new-version banner.
export function getEnvBadgeClass(env: string | undefined): string {
    if (env === 'local') return 'env-badge--local';
    if (env === 'testing') return 'env-badge--testing';
    if (env === 'staging') return 'env-badge--staging';
    return 'env-badge--prod';
}

export function chunkArray(arr: any[], n: number) {
    const size = Math.ceil(arr.length / n);
    return Array.from({ length: n }, (v, i) =>
        arr.slice(i * size, i * size + size)
    );
}

export function domain(url: string, keepPath = true): string {
    if (!url) {
        return url;
    }

    let domainStr = url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\?.+$/, '')
        .replace(/#.+$/, '');

    if (!keepPath) {
        domainStr = domainStr.split('/')[0];
    }

    return domainStr;
}

export function randomString(length: number): string {
    const randomString = (Math.random() + 1).toString(36).substring(7);

    return randomString.substring(0, length);
}

export function displayNumber(number: number) {
    return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function NgbDateToString(date: NgbDateStruct | null, delimiter = '-') {
    let twoDigit = function (number: number): string {
        return (number < 10 ? '0' : '') + number;
    }

    return date
        ? date.year + '-' + twoDigit(date.month) + '-' + twoDigit(date.day)
        : '';
}

export function setupFormDateRange(form: FormGroup, keyFrom: string, keyTo: string) {
    form.get(keyFrom)?.valueChanges.subscribe((newValue) => {
        if (!newValue || !newValue.match(/\d\d\d\d-\d\d-\d\d/)) return;

        let fromDate = new Date(newValue);
        let toDate = new Date(form.value[keyTo]);

        if (fromDate.getTime() > toDate.getTime()) {
            form.get(keyTo)?.setValue(newValue);
        }
    });

    form.get(keyTo)?.valueChanges.subscribe((newValue) => {
        if (!newValue || !newValue.match(/\d\d\d\d-\d\d-\d\d/)) return;

        let fromDate = new Date(form.value[keyFrom]);
        let toDate = new Date(newValue);

        if (toDate.getTime() < fromDate.getTime()) {
            form.get(keyFrom)?.setValue(newValue);
        }
    });
}

export function isUrlValid(url: string): boolean {
    try {
        let urlObj = new URL(url);
        // Check if protocol is http or https
        if (!['http:', 'https:'].includes(urlObj.protocol)) {
            return false;
        }
        // Check if hostname is at least 3 characters (e.g., a.b)
        if (urlObj.hostname.length < 3) {
            return false;
        }
        // Check if hostname contains at least one dot
        if (!urlObj.hostname.includes('.')) {
            return false;
        }
        return true;
    } catch (e) {
        return false;
    }
}

/** A typed link: trimmed, with https:// added when it has no http(s) protocol ('' when empty). */
export function normalizeLinkUrl(url: string | null | undefined): string {
    const trimmed = (url || '').trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : 'https://' + trimmed;
}

/** A well-formed link (after normalizeLinkUrl): isUrlValid and no spaces. */
export function isLinkUrlValid(url: string): boolean {
    return !!url && !/\s/.test(url) && isUrlValid(url);
}

/** Max length of a Support Doc's notes (stored as TEXT). */
export const SUPPORT_DOC_NOTES_MAX_LENGTH = 65000;

export function isEmailValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export function capitalizeFirstLetters(str: string): string {
    if (!str) return str;

    return str.replace(/\b\w/g, function (char) {
        return char.toUpperCase();
    });
}

export function formatDate(sourceDate: string | null | undefined, outputFormat: string, sourceFormat?: string): string {
    if (!sourceDate) return '';
    const momentFn = moment as unknown as (date: string, format?: string) => moment.Moment;
    return momentFn(sourceDate, sourceFormat).format(outputFormat);
}

export function textToSlug(text: any): string {
    return slugify(text, { lower: true })
        .replace(/[^a-z0-9-]/g, '')
        .replace(/(^-|-$)/g, '')
        .replace(/-+/g, '-');
}

export function roundIfFloat(value: any, decimals: number): number {
    if (typeof value === 'number' && !Number.isInteger(value)) {
        return parseFloat(value.toFixed(decimals));
    }

    return value;
}

export function getTableTitle(tableName: string) {
    if (tableName == 'operator_brand_game_type_countrys') return 'Welcome offer';
    if (tableName == 'operator_brands') return 'iGaming Brand';
    return tableName
}

export function getUserInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ')
        .map(p => p.replace(/^[^a-zA-Z]+/, ''))
        .filter(p => p.length > 0);
    if (parts.length === 0) return '';
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
}

const COMPANY_SUFFIXES = new Set([
    'ltd', 'llc', 'llp', 'inc', 'corp', 'co', 'gmbh', 'ag', 'ab', 'oy', 'oyj',
    'as', 'asa', 'nv', 'bv', 'sa', 'sl', 'srl', 'sro', 'sia', 'pte', 'plc',
    'se', 'limited', 'kg', 'kft', 'doo', 'ehf', 'aps', 'sarl', 'ltda', 'pty', 'kk',
]);

export function getPublisherInitials(name: string): string {
    if (!name) return '';
    let words = name.trim().split(/\s+/)
        .map(w => w.replace(/^[^a-zA-Z]+/, ''))
        .filter(w => w.length > 0);

    // Drop trailing legal-entity suffixes (e.g. "Hoku Entertainment B.V." -> "Hoku Entertainment")
    // so they don't get mistaken for the company's second meaningful word.
    while (words.length > 1 && COMPANY_SUFFIXES.has(words[words.length - 1].replace(/[^a-zA-Z]/g, '').toLowerCase())) {
        words = words.slice(0, -1);
    }

    if (words.length === 0) return '';
    if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();

    // Single-word name (e.g. "ChoixMedia", "GLTest") - use its internal capitals as word boundaries
    const word = words[0];
    const capitals = word.match(/[A-Z]/g) || [];
    if (capitals.length >= 2) return (capitals[0] + capitals[1]).toUpperCase();

    return word.slice(0, 2).toUpperCase();
}

export function isNewDate(date: string | Date | null | undefined, days: number = 7): boolean {
    if (!date) return false;
    const created = new Date(date).getTime();
    if (Number.isNaN(created)) return false;
    return Date.now() - created <= days * 24 * 60 * 60 * 1000;
}

export function removeHtmlTags(str: string) {
    if (!str) return str;

    return str.replace(/<[^>]*>/g, '');
}

export function quarterToMonths(quarterStr: string): string {
    // Parse quarter string (e.g., "Q1 2025")
    const match = quarterStr.match(/Q([1-4])\s+(\d{4})/);
    if (!match) {
        throw new Error('Invalid quarter format. Expected format: "Q1 2025"');
    }

    const quarter = parseInt(match[1]);
    const year = parseInt(match[2]);

    // Calculate start month for the quarter (0-based)
    const startMonth = (quarter - 1) * 3;

    // Generate the three months for the quarter
    const months = Array.from({ length: 3 }, (_, i) => {
        const month = startMonth + i + 1; // +1 because months are 1-based
        return `${year}-${month.toString().padStart(2, '0')}-01`;
    });

    return months.join(',');
}

import { AlertService } from "../services/alerts.service";

export async function copyToClipboard(text: string, alertService?: AlertService): Promise<boolean> {
    if (!text) return false;

    let success = false;

    // Try modern API first
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            success = true;
        } catch (err) {
            console.error('Clipboard API failed', err);
        }
    }

    if (!success) {
        // Fallback for non-secure context or incompatible browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            success = document.execCommand('copy');
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textArea);
    }

    if (alertService) {
        if (success) {
            alertService.alertSuccess('Copied');
        } else {
            alertService.alertError('Failed to copy');
        }
    }

    return success;
}

export function formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    // Determine the index of the unit (0 = Bytes, 1 = KB, etc.)
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Calculate result: bytes / (1024 ^ i)
    const result = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${result} ${sizes[i]}`;
}